// Offline mode: where the cache and the queue live. One small interface, two backends:
//   idbStore()    IndexedDB in the browser (database "hc-offline")
//   memoryStore() a Map, for the tests
// Stores: cache (user|url -> a read's answer), rows (user|table|id -> one row), queue (user|seq -> a waiting change),
// meta (user -> { lastSync, permHash }). Every key starts with the login's user id, so a login only ever touches its own.

export const STORES = ['cache', 'rows', 'queue', 'meta']

export function memoryStore() {
  const db = Object.fromEntries(STORES.map(s => [s, new Map()]))
  const clone = v => (v === undefined ? undefined : typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)))
  return {
    async get(store, key) { return clone(db[store].get(key)) },
    async put(store, key, value) { db[store].set(key, clone(value)) },
    async del(store, key) { db[store].delete(key) },
    async list(store, prefix = '') { return [...db[store].entries()].filter(([k]) => k.startsWith(prefix)).sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([k, v]) => ({ key: k, value: clone(v) })) },
    async clear(store, prefix = '') { for (const k of [...db[store].keys()]) if (k.startsWith(prefix)) db[store].delete(k) },
    _db: db,
  }
}

export function idbStore(name = 'hc-offline') {
  let opening = null
  const open = () => opening || (opening = new Promise((res, rej) => {
    const r = indexedDB.open(name, 1)
    r.onupgradeneeded = () => { for (const s of STORES) if (!r.result.objectStoreNames.contains(s)) r.result.createObjectStore(s) }
    r.onsuccess = () => res(r.result)
    r.onerror = () => { opening = null; rej(r.error) }
  }))
  const tx = async (store, mode, fn) => {
    const db = await open()
    return new Promise((res, rej) => {
      const t = db.transaction(store, mode), s = t.objectStore(store)
      let out
      Promise.resolve(fn(s, v => { out = v })).catch(rej)
      t.oncomplete = () => res(out)
      t.onerror = () => rej(t.error)
      t.onabort = () => rej(t.error)
    })
  }
  const range = prefix => (prefix ? IDBKeyRange.bound(prefix, prefix + '￿') : undefined)
  return {
    get: (store, key) => tx(store, 'readonly', (s, set) => { const r = s.get(key); r.onsuccess = () => set(r.result) }),
    put: (store, key, value) => tx(store, 'readwrite', s => { s.put(value, key) }),
    del: (store, key) => tx(store, 'readwrite', s => { s.delete(key) }),
    list: (store, prefix = '') => tx(store, 'readonly', (s, set) => {
      const out = [], c = s.openCursor(range(prefix))
      c.onsuccess = () => { const cur = c.result; if (cur) { out.push({ key: cur.key, value: cur.value }); cur.continue() } else set(out) }
    }),
    clear: (store, prefix = '') => tx(store, 'readwrite', s => { if (prefix) s.delete(range(prefix)); else s.clear() }),
  }
}
